import React, { useState, useEffect } from "react";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import ModeRoundedIcon from "@mui/icons-material/ModeRounded";
import {
  addNewCertificateField,
  editCertificateField,
  loadCertificateField,
} from "../../../store/admin/actions";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import SearchBox from "../../common/components/SearchBox";
import TableSkeleton from "../../common/components/TableSkeleton";
import ReactPaginate from "react-paginate";

const CertificateFields = (props) => {
  const {
    createCertificateField,
    setCreateCertificateField,
    // redux -----------------
    loading,
    certificateFields,
    addNewCertificateField,
    editCertificateField,
    loadCertificateField,
  } = props;
  const [loadingData, setLoadingData] = useState(true);
  const [fieldId, setFieldId] = useState(0);
  const [fieldName, setFieldName] = useState("");
  const [formError, setFormError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  //PAGINATION
  const [itemOffset, setItemOffset] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    if (certificateFields === null || certificateFields.length === 0) {
      loadCertificateField(setLoadingData);
    } else {
      setLoadingData(false);
    }

    return () => setLoadingData(true);
  }, []);

  const onCloseForm = () => {
    setCreateCertificateField(false);
    setFieldId(0);
    setFieldName("");
    setFormError(null);
  };

  const onSaveForm = () => {
    if (fieldId === 0 && !createCertificateField) {
      setFormError({
        element: "GENERAL",
        msg: "Please select certificate field you want to update.",
      });
      return;
    }
    if (fieldName.length === 0) {
      setFormError({
        element: "NAME",
        msg: "Certificate field name is required.",
      });
      return;
    }

    if (fieldId > 0 && !createCertificateField) {
      editCertificateField(
        { id: fieldId, name: fieldName },
        (status) => status && onCloseForm()
      );
    } else {
      addNewCertificateField(fieldName, (status) => status && onCloseForm());
    }
  };

  const IS_EDITING = Boolean(fieldId > 0);

  const SEARCHED_DATA = !loadingData
    ? searchInput.length === 0 && certificateFields
      ? certificateFields
      : certificateFields.filter((item) =>
          item.name
            .toLocaleLowerCase()
            .includes(searchInput.toLocaleLowerCase())
        )
    : [];
  const paginate = (items) => {
    const endOffset = itemOffset + itemsPerPage;
    return items.slice(itemOffset, endOffset);
  };

  const handlePageClick = (event) => {
    const newOffset =
      (event.selected * itemsPerPage) % SEARCHED_DATA.length || 0;

    setItemOffset(newOffset);
  };
  const PAGE_COUNT = Math.ceil(SEARCHED_DATA.length / itemsPerPage);
  const PAGE_DATA = paginate(SEARCHED_DATA);

  return (
    <div className="my-2 elevated rounded p-2">
      <div className="mb-2">
        <SearchBox
          className="mb-1"
          onSearch={setSearchInput}
          disabled={
            loadingData ||
            certificateFields === null ||
            certificateFields.length === 0
          }
          placeholder="Search certificate..."
        />
      </div>

      <table className="table table-striped table-hover table-sm  fixTableHead mt-2">
        <thead>
          <tr style={{ backgroundColor: "#f0f2f5" }}>
            <th scope="col">#</th>
            <th scope="col"> Certificate Field </th>
            <th className="text-center">Action</th>
          </tr>
        </thead>

        {loadingData || certificateFields === null ? (
          <TableSkeleton cols={3} />
        ) : certificateFields.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={3}>
                <div className="text-center font-weight-bold">
                  No certificate field found!
                </div>
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {PAGE_DATA.map((certificateItem, index) => (
              <tr key={"certificate-" + index}>
                <th scope="row" style={{ backgroundColor: "#f0f2f5" }}>
                  {index + 1 + itemOffset}
                </th>

                <td>{certificateItem.name}</td>
                <td align="center">
                  {" "}
                  <IconButton
                    aria-label="edit certificate"
                    size="small"
                    onClick={() => {
                      setFieldId(certificateItem.id);
                      setFieldName(certificateItem.name);
                    }}
                  >
                    <ModeRoundedIcon fontSize="small" />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>

      {PAGE_COUNT > 1 && (
        <ReactPaginate
          breakLabel="..."
          nextLabel={
            <span>
              <span className="pr-1 d-none d-md-inline">Next</span>
              <i className="fas fa-angle-double-right"></i>
            </span>
          }
          previousLabel={
            <span>
              <i className="fas fa-angle-double-left"></i>
              <span className="pl-1 d-none d-md-inline">Previous</span>
            </span>
          }
          pageRangeDisplayed={3}
          marginPagesDisplayed={1}
          onPageChange={handlePageClick}
          pageCount={PAGE_COUNT}
          renderOnZeroPageCount={null}
          containerClassName="pagination mt-2"
          pageLinkClassName="btn btn-outline-info btn-sm mr-1 ml-1 mt-1"
          previousLinkClassName="btn btn-outline-info btn-sm mr-1 ml-1 mt-1"
          nextLinkClassName="btn btn-outline-info btn-sm ml-1 mt-1"
          activeLinkClassName="active"
        />
      )}

      {!loadingData && (
        <Dialog
          onClose={onCloseForm}
          aria-labelledby="customized-dialog-title"
          open={Boolean(createCertificateField || IS_EDITING)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle className="text-primary">
            <Typography variant="overline" display="block">
              {IS_EDITING
                ? "Edit Certificate field"
                : "Add New Certificate field"}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={onCloseForm}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <span className="material-icons">close</span>
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {formError && formError.element === "GENERAL" && (
              <Alert severity="error" className="mb-1">
                {formError.msg}
              </Alert>
            )}

            <TextField
              id="outlined-basic"
              label="Certificate field name"
              variant="outlined"
              size="small"
              className="w-100"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
            />
            {formError && formError.element === "NAME" && (
              <span className="text-danger" style={{ fontSize: "13px" }}>
                {formError.msg}
              </span>
            )}
          </DialogContent>
          <DialogActions className="d-flex justify-content-center py-3">
            <button
              onClick={onSaveForm}
              type="button"
              className="btn btn-primary text-uppercase  px-4"
              disabled={loading}
            >
              {loading ? "Wait..." : "Save"}
            </button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

CertificateFields.propTypes = {
  createCertificateField: PropTypes.bool,
  setCreateCertificateField: PropTypes.func,
};

const mapStateToProps = ({ loading, certificate }) => {
  return {
    loading,
    certificateFields: certificate.certificateFields,
  };
};

const mapDispatchToProps = {
  addNewCertificateField,
  editCertificateField,
  loadCertificateField,
};

export default connect(mapStateToProps, mapDispatchToProps)(CertificateFields);
